<template>
  <DialogTemplateToolkit
    class="save-criteria-template"
    :data-qa="edit ? 'dialog-edit-criteria-template' : 'dialog-save-criteria-template'"
    :dialog="dialog"
    first_btn="Common.Save"
    :loading="load_next"
    :retain-focus="retainFocus"
    :second_btn="edit ? 'Common.Cancel' : 'Common.Skip'"
    :title="edit ? $t('Tenders.EditCriteriaTemplate') : $t('Tenders.SaveCriteriaAsTemplate')"
    @apply="checkApply"
    @close="close">
    <template #activator-btn>
      <MenuItem
        v-if="edit"
        data-qa="item-edit"
        icon="edit"
        :label="$t('Tenders.ChangeBindingConditions').toUpperCase()"
        small
        @click="open"/>
      <sButton
        v-else
        :disabled="disabled"
        @click="open">
        {{ $t('Common.Next') }}
      </sButton>
    </template>
    <template #card-text>
      <sForm
        ref="save-criteria-template-form"
        class="save-criteria-template__form"
        :disabled="loading"
        label-position="top"
        :model="{ name, category, biddingType, isDefault }"
        :rules="rules">
        <sFormItem
          ref="template-name"
          class="input"
          :label="$t('Planning.Name')"
          prop="name">
          <sInput
            class="save-criteria-template__name "
            clearable
            data-qa="template-name"
            :max-length="4000"
            max-rows="10"
            :placeholder="$t('Planning.Name')"
            :readonly="load_next"
            rows="1"
            type="textarea"
            :value.sync="name"/>
        </sFormItem>
        <div
          class="flex flex-between mt-8"
          no-gutters>
          <RadioGroup
            class="save-criteria-template__use-as-default"
            class-name="mt-2"
            data-qa="use-as-default"
            :disabled="load_next"
            :header="$t('Tenders.BindTemplateAsPrimaryForAllTenders')"
            :model.sync="isDefault"
            @change="changeDefault"/>
          <sAlert
            class="my-0 py-3 px-4"
            width="240px">
            {{ $t('Tenders.TheMainTemplateForAllTenders') }}
            <template slot="desc">
              {{ $t('Tenders.WillBeAddedToAllTendersOfOrganization') }}
            </template>
          </sAlert>
        </div>
        <template v-if="!isDefault">
          <RadioGroup
            class="save-criteria-template__link-by-category mt-2"
            class-name="mt-2 pt-2 category"
            data-qa="category"
            :disabled="load_next"
            :header="$t('Tenders.LinkTemplateToCategory')"
            :model.sync="by_category"
            @change="setDefaultCategory"/>

          <sFormItem
            v-if="by_category"
            ref="template-category"
            class="mt-4 d-flrx flrx-column input"
            :label="$t('Tenders.Category')"
            prop="category.classificationCode">
            <div
              v-if="categoryLoading"
              style="height: 40px; border-radius: 31px; background: #cdcdcd;"/>
            <CategoryDropdownTree
              v-else
              ref="CategoryDropdownTree"
              class="save-criteria-template__choose-category mt-2 category "
              data-qa="use-category"
              input-type="textarea"
              :label="$t('Tenders.Category')"
              :model.sync="category"
              return-object
              @changeState="changeStateCategoryDropdownTree"
              @update:model="sendChangeFormItem"/>
          </sFormItem>
          <RadioGroup
            class="save-criteria-template__link-by-type mt-8"
            class-name="mt-2"
            data-qa="use-biddingType"
            :disabled="load_next"
            :header="$t('Tenders.BindTemplateToTenderType')"
            :model.sync="by_type"
            @change="setDefaultType"/>
          <sFormItem
            v-if="by_type"
            ref="template-type"
            class="mt-4 input"
            :label="$t('Tenders.TenderType')"
            prop="biddingType.biddingTypeId">
            <sSelect
              class="save-criteria-template__choose-type input"
              clearable
              data-qa="biddingType"
              :disabled="load_next"
              filterable
              :loading="loading"
              :placeholder="$t('Tenders.TenderType')"
              :value.sync="biddingType"
              value-key="biddingTypeId"
              @update:value="sendChangeFormItem">
              <sOption
                v-for="item in bidding_types"
                :key="`bidding-types-${item.biddingTypeId}`"
                :data-qa="`biddingType-item-${item.biddingTypeId}`"
                :label="item.titleRu"
                :value="item"/>
            </sSelect>
          </sFormItem>
        </template>
        <div
          v-if="show_locking"
          class="flex flex-between mt-8">
          <RadioGroup
            class="save-criteria-template__locked"
            class-name="mt-2"
            data-qa="lock_card"
            :disabled="load_next"
            :header="$t('Tenders.ProhibitChangeOfCriteriaCard')"
            :model.sync="isLocked"/>
          <sAlert
            width="230px">
            {{ $t('Tenders.ProhibitChangeOfCriteriaCardTitle') }}
            <template slot="desc">
              {{ $t('Tenders.ProhibitChangeOfCriteriaCardText') }}
            </template>
          </sAlert>
        </div>
      </sForm>
    </template>
  </DialogTemplateToolkit>
</template>
<script>
import MenuItem from 'components/common/MenuItem.vue'
import RadioGroup from 'components/common/inputs/RadioGroup.vue'
import CategoryDropdownTree from 'components/common/dialogs/CategoryDropDownTree.vue'
import DialogTemplateToolkit from 'components/common/dialogs/DialogTemplateToolkit.vue'

import { validateRuls } from 'mixins'
import godModeMixin from 'mixins/godModeMixin'
import SmarttenderApi from '@/api/smarttenderApi'
const api = new SmarttenderApi()

export default {
  name: 'saveCriteriaAsTemplate',
  mixins: [ validateRuls, godModeMixin ],
  data() {
    const that = this
    return {
      categoryLoading: false,
      dialog: false,
      isDefault: false,
      isLocked: false,
      by_type: false,
      by_category: false,
      biddingTypeId: null,
      defaultClassification: null,
      classificationCode: null,
      biddingType: {},
      category: {},
      bidding_types: [],
      name: '',
      loading: false,
      load_next: false,
      all_templates: [],
      retainFocus: true,
      rules: {
        'biddingType.biddingTypeId': {
          required: true,
          trigger: 'change',
          validator: (rule, value, callback) => {
            if (!value && that.by_type && !that.isDefault) {
              callback(new Error(that.$t('Validate.Required')))
            }
            if (that.checkExtend()) {
              callback(new Error(that.$t('Tenders.TemplateIsAlreadyAttachedChangeConditions')))
            }
            callback()
          },
        },
        'category.classificationCode': {
          required: true,
          trigger: 'change',
          validator: (rule, value, callback) => {
            if (!value && that.by_category && !that.isDefault) {
              callback(new Error(that.$t('Validate.Required')))
            }
            if (that.checkExtend()) {
              callback(new Error(that.$t('Tenders.TemplateIsAlreadyAttachedChangeConditions')))
            }
            callback()
          },
        },
        name: [
          {
            required: true,
            trigger: 'change',
            validator: (rule, value, callback) => {
              if (!value) {
                callback(new Error(that.$t('Validate.Required')))
              }
              if (value.length > 254) {
                callback(new Error(that.$t('Validate.maxLength', {number: 254, type: this.$tc('Common.characters', 4) })))
              }
              if (that.all_templates.some(el => el.name == value?.trim() && that.card?.templateId != el.templateId)) {
                callback(new Error(that.$t('Tenders.TheTemplateNameMustBeUnique')))
              }
              callback()
            },
          }
        ],
      },
    }
  },
  methods: {
    changeStateCategoryDropdownTree(state) {
      this.retainFocus = !state
    },
    /**
   * Подставляем категорию из тендера. Задача #37wj4nn
   */
    setDefaultCategory(needChange) {
      if (needChange && this.tender && this.tender.classificationCode) {
        if (this.defaultClassification) {
          this.$set(this, 'category', this.defaultClassification)
          this.$refs.CategoryDropdownTree.model_category = this.defaultClassification
        } else {
          this.categoryLoading = true
          api.searchClassifications(this.tender.classificationCode)
            .then((result) => {
              if (result?.classifications?.length) {
                this.defaultClassification = result.classifications[0]
                this.$set(this, 'category', result.classifications[0])
              }
            })
            .finally(() => {
              this.categoryLoading = false
              this.$nextTick(() => {
                if (this.$refs.CategoryDropdownTree) {
                  this.$refs.CategoryDropdownTree.model_category = this.defaultClassification
                }
              })
            })
        }
      } else {
        this.$set(this, 'category', null)
      }
    },
    /**
   * Подставляем тип из тендера. Задача #37wj4nn
   */
    setDefaultType(needChange) {
      if (needChange && this.tender && this.tender.biddingType) {
        this.$set(this, 'biddingType', this.tender.biddingType)
      } else {
        this.$set(this, 'biddingType', null)
      }
    },
    changeDefault(value) {
      if (value) {
        this.by_category = false
        this.by_type = false
      }
    },
    sendChangeFormItem() {
      this.$refs['template-category']?.$emit.apply(this.$refs['template-category'], ['s.form.change'].concat(this.category?.classificationCode))
      this.$refs['template-type']?.$emit.apply(this.$refs['template-type'], ['s.form.change'].concat(this.biddingType?.biddingTypeId))
    },
    checkExtend() {
      return this.all_templates.some(el => {
        let not_me = this.card?.templateId != el.templateId,
          same_cat = el.classificationCode == this.category?.classificationCode,
          same_type = el.biddingTypeId == this.biddingType?.biddingTypeId
        return same_cat && same_type && not_me
      })
    },
    async open() {
      let loadBdTypes = () => {
        this.loading = true
        this.$store.dispatch('getCriteriaCardTemplates').then(res => {
          this.all_templates = res
        })
        this.$store.dispatch('setBiddingTypes')
          .then(res => this.bidding_types = res)
          .finally(() => this.loading = false)
      }
      if (this.edit) {
        if (this.card.biddingTypeId) {
          this.setDefaultType(true)
        }
        this.name = this.card.name
        this.isDefault = this.card.isDefault
        this.by_type = !!this.card.biddingTypeId
        this.by_category = !!this.card.classificationCode
        this.category = this.card.classificationCode
        if (this.biddingType) {
          this.biddingType.biddingTypeId = this.card.biddingTypeId
        }
        this.isLocked = this.card.isLocked !== 'UNLOCKED'
        loadBdTypes()
        setTimeout(() => {
          this.dialog = true
        }, 100)
        return
      }
      try {
        this.$root.$emit('showOverlay')
        await this.openFunc().finally(() => this.$root.$emit('closeOverlay'))

        if (this.isAccesToCriteriaTemplate) {
          this.$Modal.confirm({
            title: this.$t('Tenders.CriteriaAsTemplate'),
            content: this.$t('Tenders.CriteriaAsTemplateText'),
            closable: false,
            loading: true,
            dataQa: 'criteria-as-template-confirm',
            okText: this.$t('Common.Save'),
            cancelText: this.$t('Common.Skip'),
            onOk: () => {
              loadBdTypes()
              this.$Modal.remove()
              this.dialog = true
            },
            onCancel: this.close,
            width: 736
          })
        } else {
          this.close()
        }
      } catch (error) {
        this.$log_error(error.message || error)
        this.$notice.warning({
          message: error.message,
          showClose: true,
          progress: true,
          duration: 20
        })
      }
    },
    checkApply() {
      this.$refs['save-criteria-template-form'].validate()
        .then(() => {
          let old = this.all_templates.find(el => el.isDefault && this.card?.templateId != el.templateId)
          if (this.isDefault && old) {
            this.$Modal.confirm({
              title: this.$t('Tenders.ReplaceTheMainCriteriaTemplateForAllTenders'),
              text: this.$t('Tenders.DoYouWantToReplaceTheMainTemplateForFurtherTenders', {name: old.name}),
              closable: true,
              okText: this.$t('Common.Replace'),
              cancelText: this.$t('Common.Cancel'),
              loading: true,
              onOk: () => {
                this.$Modal.remove()
                this.apply()
              },
              onCancel: this.$Modal.remove,
              width: 736
            })
          } else {
            this.apply()
          }
        })
        .catch(() => {
          this.$notice.warning({
            title: this.$t('Common.PleaseNote'),
            message: this.$t('Validate.checkRequiredFelds'),
            showClose: true,
            progress: true,
            duration: 10
          })
        })
    },
    async apply() {
      this.load_next = true

      try {
        const resultModel = {
          classificationCode: this.category?.classificationCode,
          cardId: null,
          biddingTypeId: this.biddingType?.biddingTypeId,
          title: this.name.trim(),
          isDefault: this.isDefault,
          templateId: this.card.templateId,
          isLocked: this.isLocked,
        }
        if (this.edit) {
          resultModel.cardId = this.card?.id || this.card?.code
          resultModel.templateId = this.card.templateId
          await api.updateCriteriaCardTemplate(resultModel)
        } else {
          resultModel.cardId = this.card?.id
          await api.addCriteriaCardTemplate(resultModel)
        }
        this.close(true)
      } catch (error) {
        this.$log_error(error?.message)
        this.$show_error_message(error?.message)
      }
      this.load_next = false
    },
    async close(add_info) {
      await this.nextFunc(add_info ? {
        name: this.name.trim(),
        category: this.category,
        biddingType: this.biddingType,
        isDefault: this.isDefault,
        isLocked: this.isLocked,
      } : null)
      this.dialog = false
      this.confirm = true
    }
  },
  computed: {
    show_locking() {
      return this.$acl.check('tenderAdmin') &&
   (
     (this.by_type && this.biddingType?.biddingTypeId) ||
    (this.by_category && this.category?.classificationCode) ||
    this.isDefault
   )
    }
  },
  created() {
    this.rules.name.push(this.commentValidateRule[0])
  },
  props: {
    disabled: Boolean,
    nextFunc: {
      type: Function,
      default: () => {}
    },
    openFunc: {
      type: Function,
      default: async () => {}
    },
    card: Object,
    edit: Boolean,
    tender: {
      type: Object
    }
  },
  components: {
    MenuItem,
    RadioGroup,
    CategoryDropdownTree,
    DialogTemplateToolkit
  }
}
</script>
<style lang="scss" scoped>
.save-criteria-template {
 &__form .input {
  width: 400px;
 }
}
</style>
